import { Container } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { stateLocation } from "src/Interfaces/paymentFail";
import styled from "styled-components";
import { buttonGradient, ssCheckoutFlow, theme, CheckoutFlowContext } from "@utilities";
import { PATH_CART, PATH_BOOK_NOW, PATH_QUOTES } from "@routes";
import { useMemo, useCallback } from "react";

const PaymentFail = () => {
    const location: stateLocation = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get error message from navigation state or URL query params
    const errorMessage = useMemo(() => {
        // First check navigation state
        if (location?.state?.message) {
            return location.state.message;
        }
        // Then check URL query params (in case backend redirects directly here)
        const detailedMessage = searchParams.get("cmsgw_Message");
        const shortMessage = searchParams.get("msg");
        return detailedMessage || shortMessage || null;
    }, [location?.state?.message, searchParams]);

    // Get checkout flow context from navigation state or sessionStorage
    const checkoutFlow: CheckoutFlowContext | null = useMemo(() => {
        // First check navigation state (cast to any to avoid type issues with extended state)
        const state = location?.state as { checkoutFlow?: CheckoutFlowContext } | undefined;
        if (state?.checkoutFlow) {
            return state.checkoutFlow;
        }
        // Fallback to sessionStorage (in case of page reload)
        return ssCheckoutFlow.getItem();
    }, [location?.state]);

    // Determine the return path based on checkout flow
    const { returnPath, buttonText } = useMemo(() => {
        if (!checkoutFlow) {
            return { returnPath: PATH_CART, buttonText: "Return to Cart" };
        }

        switch (checkoutFlow.type) {
            case "book-now":
                return {
                    returnPath: checkoutFlow.sessionId
                        ? `${PATH_BOOK_NOW}?sessionId=${checkoutFlow.sessionId}`
                        : PATH_BOOK_NOW,
                    buttonText: "Return to Booking",
                };
            case "quote":
                return {
                    returnPath: checkoutFlow.quoteId
                        ? `${PATH_QUOTES}/${checkoutFlow.quoteId}`
                        : PATH_QUOTES,
                    buttonText: "Return to Quote",
                };
            case "cart":
            default:
                return {
                    returnPath: checkoutFlow.sessionId
                        ? `${PATH_CART}?sessionId=${checkoutFlow.sessionId}`
                        : PATH_CART,
                    buttonText: "Return to Cart",
                };
        }
    }, [checkoutFlow]);

    // Handle return button click
    const handleReturn = useCallback(() => {
        ssCheckoutFlow.removeItem();
        sessionStorage.removeItem("guestCheckoutInProgress");
        navigate(returnPath);
    }, [navigate, returnPath]);

    return (
        <Container>
            <FailOrder>
                <div className="orderFailBackground">
                    <div className="orderFail">
                        <img className="svg" src="/images/fi_x.svg" alt="order_fail" />
                    </div>
                </div>
                <p className="heading">Payment Failed</p>
                <div className="subHeading">
                    <p className="mainError">Payment attempt failed</p>
                    {errorMessage && <p className="detailedError">{errorMessage}</p>}
                    <p className="suggestion">Please try again with a different payment method.</p>
                </div>
                <RetryButton onClick={handleReturn}>{buttonText}</RetryButton>
            </FailOrder>
        </Container>
    );
};

const FailOrder = styled.div`
    max-width: 500px;
    margin: 60px auto;
    padding: 40px 24px;
    background: white;
    border-radius: ${theme.borderRadius.large};
    text-align: center;

    .orderFailBackground {
        background:
            linear-gradient(0deg, #ffffff -20.99%, rgba(255, 255, 255, 0.17) 119.75%), #f2f2f2;
        height: 78px;
        width: 78px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        margin: 0 auto;

        .orderFail {
            height: 60px;
            width: 60px;
            background-color: #dc3545;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 50%;

            .svg {
                width: 70%;
                fill: white;
            }
        }
    }

    .heading {
        font-size: 24px;
        font-weight: 700;
        line-height: 32px;
        text-align: center;
        margin-top: 20px;
        margin-bottom: 16px;
        color: #131214;
    }

    .subHeading {
        margin-bottom: 24px;
        text-align: center;

        .mainError {
            font-size: 16px;
            font-weight: 600;
            color: #dc3545;
            margin-bottom: 12px;
            line-height: 24px;
        }

        .detailedError {
            font-size: 14px;
            color: #666;
            line-height: 22px;
            margin-bottom: 16px;
            padding: 12px 16px;
            background: #fff5f5;
            border-radius: 8px;
            border: 1px solid #ffebeb;
        }

        .suggestion {
            font-size: 14px;
            color: #4f4f4f;
            line-height: 22px;
        }
    }
`;

const RetryButton = styled.button`
    background: ${buttonGradient(theme.gradients.button)};
    color: ${theme.colors.black};
    border: none;
    padding: 14px 32px;
    border-radius: ${theme.borderRadius.medium};
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.9;
    }
`;

export default PaymentFail;
